import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { Icon } from '@core/enums/icon.enums';
import { MyHivveAppClient } from '@hivvetech/myhivve-oidc-client';
import { IAccountProfile } from '@core/models/account-profile.model';
import {
  AccountService,
  AppLauncherService,
  AuthService,
  getProductDisplayName,
  Logger,
  MyHivveDashboardAppClient
} from '@core/services';
import { AuthRedirectionService } from '@core/services/auth/auth-redirection.service';
import { environment } from '@env';
import { Subject, takeUntil } from 'rxjs';

const log = new Logger('PrivateTopNavbarComponent');

@Component({
  selector: 'app-private-top-navbar',
  templateUrl: './private-top-navbar.component.html',
  styleUrls: ['./private-top-navbar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PrivateTopNavbarComponent implements OnInit, OnDestroy {
  Icon = Icon;
  environment = environment;
  apps: MyHivveDashboardAppClient[] = [];
  launchingApp: MyHivveDashboardAppClient | null = null;
  acountProfile!: IAccountProfile;

  @Input() isCollapsed = false;
  @Output() toggleEvent = new EventEmitter<boolean>();

  private MIN_WIDTH_SIZE = 576;
  private MAX_WIDTH_SIZE = 991.98;
  private stop$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private authRedirect: AuthRedirectionService,
    private accountService: AccountService,
    private appLauncherService: AppLauncherService
  ) {}

  ngOnInit(): void {
    this.accountService.getUserDetails.pipe(takeUntil(this.stop$)).subscribe(res => {
      if (res) {
        this.acountProfile = res;
      }
    });

    this.appLauncherService
      .loadApps({ region: this.authService.user?.region })
      .pipe(takeUntil(this.stop$))
      .subscribe(apps => {
        this.apps = apps;
      });
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  async launchApp(event: MouseEvent, app: MyHivveDashboardAppClient): Promise<void> {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    if (this.launchingApp) {
      return;
    }

    this.launchingApp = app;
    await this.waitForNextPaint();

    try {
      await this.authRedirect.redirectToApp(app);
    } catch {
      this.launchingApp = null;
      log.warn('Product launch did not open', { app: app.name });
    }
  }

  getProductDisplayName(app: MyHivveAppClient): string {
    return getProductDisplayName(app);
  }

  async signOut(): Promise<void> {
    await this.authService.signOut(true);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const width = event.target.innerWidth;
    if (width && width >= this.MIN_WIDTH_SIZE && width <= this.MAX_WIDTH_SIZE) {
      this.isCollapsed = true;
      this.toggleEvent.emit(this.isCollapsed);
    }
  }

  onToggle() {
    this.isCollapsed = !this.isCollapsed;
    this.toggleEvent.emit(this.isCollapsed);
  }

  private waitForNextPaint(): Promise<void> {
    return new Promise(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
  }
}
